import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Alert, Paper } from '@mui/material';
import { Loader } from "../../components/loader/Loader";
import { GoogleSSOAuthenticationService } from './../../services/googleAuthenticationService';

type LoginProps = {
  config: {
    tenant: string;
    appId: string;
    apiBaseUrl: string;
    tokenEndpoint: string;
    userInfoEndpoint: string;
  };
  onLoginSuccess?: (userData: any) => void;
  onLoginFailure?: (error: any) => void;
};

export function Login({ config, onLoginSuccess, onLoginFailure }: LoginProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authService = new GoogleSSOAuthenticationService(config);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setLoading(true);
        
        //  query string (potential SSO callback)
        const queryString = window.location.search;
        
        if (queryString) {
          await handleSSOCallback(queryString);
        } else {
          // user  authenticated?
          checkAuthCookie();
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMsg);
        onLoginFailure && onLoginFailure(err);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleSSOCallback = async (queryString: string) => {
    const token = await authService.getToken(queryString);
    
    if (token) {
      const userData = await authService.getUserData(token);
      
      if (userData) {
        const now = new Date();
        const expirationTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        
        localStorage.setItem('auth_cookie', JSON.stringify({
          isAuthenticated: true,
          token: token,
          timestamp: now.toISOString(),
          expires: expirationTime.toISOString(),
          expiresIn: '1d',
          tokenType: 'bearer'
        }));
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Notify parent component
        onLoginSuccess && onLoginSuccess(userData);
      } else {
        const errorMsg = 'Could not retrieve user information';
        setError(errorMsg);
        onLoginFailure && onLoginFailure(new Error(errorMsg));
      }
    } else {
      const errorMsg = 'Failed to authenticate';
      setError(errorMsg);
      onLoginFailure && onLoginFailure(new Error(errorMsg));
    }
  };

  const checkAuthCookie = () => {
    try {
      const authCookie = localStorage.getItem('auth_cookie');
      if (!authCookie) return;
      
      const cookieData = JSON.parse(authCookie);
      
      if (!cookieData.isAuthenticated) return;
      
      if (cookieData.expires) {
        const expiryTime = new Date(cookieData.expires);
        const now = new Date();
        if (now > expiryTime) {
          localStorage.removeItem('auth_cookie');
          return;
        }
      }
      
      const email = localStorage.getItem('username');
      if (email) {
        onLoginSuccess && onLoginSuccess({ email });
      } else {
        getUserFromToken(cookieData.token);
      }
    } catch (error) {
      console.error("Error parsing auth cookie:", error);
    }
  };

  const getUserFromToken = async (token: string) => {
    try {
      const userData = await authService.getUserData(token);
      if (userData && userData.emailId) {
        localStorage.setItem('username', userData.emailId);
        onLoginSuccess && onLoginSuccess(userData);
      }
    } catch (error) {
      console.error('Error getting user data:', error);
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await authService.login();
    } catch (error: any) {
      setLoading(false);
      const errorMsg = error.message || 'Failed to initiate login';
      setError(errorMsg);
      onLoginFailure && onLoginFailure(error);
    }
  };

  return (<>
    {loading ? (
      <Loader sx={{ position: "absolute", top: "55%" }} />
    ) : (
      <Box
        display="flex"
        justifyContent="center"
        height="25%"
        sx={{ marginTop: '20px' }}
      >
        <Paper elevation={3} sx={{ p: 5, borderRadius: 4, width: 350 }}>
          <Typography variant="h5" gutterBottom align="center">
            Login with Google SSO
          </Typography>

          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: 'var(--dashboard-theme-color)',
              ':hover': {
                background: 'var(--dashboard-theme-color)',
                boxShadow: 'none',
              },
              textTransform: 'none',
              boxShadow: 'none',
              borderRadius: '8px',
              '&.Mui-disabled': {
                color: '#7C7C7C',
                fontWeight: 600,
              },
              minWidth: '130px',
              minHeight: '37px',
              mt: 2,
            }}
            onClick={loading ? () => { } : handleLogin}
            disabled={loading}
          >
            Sign in with Google
          </Button>
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Box>
    )}
  </>
  );
}